import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { motion } from 'framer-motion';

import { QLogo } from '@quizzach/assets';
import { IAssociation, IQuestion } from '@quizzach/interfaces';
import {
	QuestionText,
	QuestionContainer,
	AssociationContainer,
} from '@quizzach/play-styles';
import { associationMotionConfig } from '@quizzach/motion';
import { AnswerReveal, PointsRow } from '@quizzach/components';

interface IAssociationQuestionProps {
	color: string;
	currentQuestionNumber: number;
	currentQuestion: IQuestion;
	totalQuestions: number;
}

interface IAssociationQuestionAnswerProps {
	questionContainerColor: string;
	answerContainerColor: string;
	currentQuestionNumber: number;
	currentQuestion: IQuestion;
	totalQuestions: number;
}

const questionCards = ['a', 'b', 'c', 'd'];

/* Open column sits full-width on top; the other three are tap cards in a
   row below it. Tapping a card swaps it to the top and sends the previous
   open column back down into the row. */
const AssociationColumns = ({
	currentQuestion,
	open,
}: {
	currentQuestion: IQuestion;
	open?: boolean;
}) => {
	const { t } = useTranslation();
	const [openColumn, setOpenColumn] = useState('a');
	const aq = currentQuestion.association_question;
	if (!aq) return null;

	return (
		<>
			<motion.div
				className='column'
				key={openColumn}
				initial={{ opacity: 0, y: -8 }}
				animate={{ opacity: 1, y: 0 }}
			>
				<div className='association-indicator'>{openColumn}</div>
				{[1, 2, 3, 4].map((number) => (
					<div className='association-option' key={`${openColumn}${number}`}>
						<div
							dangerouslySetInnerHTML={{
								__html:
									aq[`${openColumn}${number}` as keyof IAssociation] || '',
							}}
						/>
					</div>
				))}
				{open ? (
					<div className='association-option'>
						<div className='answer-text bold'>
							{aq[openColumn as keyof IAssociation]?.toUpperCase()}
						</div>
					</div>
				) : (
					<div className='association-option preview'>
						<span className='column-letter'>{openColumn}</span>
					</div>
				)}
			</motion.div>
			<div className='hidden-row'>
				{questionCards
					.filter((columnLabel) => columnLabel !== openColumn)
					.map((columnLabel) => (
						<motion.div
							key={columnLabel}
							className='association-option-hidden'
							onClick={() => setOpenColumn(columnLabel)}
							{...associationMotionConfig()}
						>
							<span className='hidden-letter'>{columnLabel}</span>
							<span className='tap-hint'>{t('quiz.tapToReveal')}</span>
							<QLogo size={16} />
						</motion.div>
					))}
			</div>
		</>
	);
};

export const AssociationQuestionMobile = (props: IAssociationQuestionProps) => {
	const { t } = useTranslation();
	const { color, currentQuestion } = props;

	return (
		<>
			<QuestionContainer color={color}>
				<QuestionText>
					<AssociationContainer>
						<AssociationColumns currentQuestion={currentQuestion} />
						<div className='association-option preview final'>
							<span className='column-letter'>
								{t('common.finalLine1')} {t('common.finalLine2')}
							</span>
						</div>
					</AssociationContainer>
				</QuestionText>
			</QuestionContainer>
			<PointsRow
				points={currentQuestion.points}
				explanation={currentQuestion.points_explanation}
			/>
		</>
	);
};

export const AssociationQuestionAnswerMobile = (
	props: IAssociationQuestionAnswerProps
) => {
	const { questionContainerColor, currentQuestion } = props;

	return (
		<>
			<QuestionContainer color={questionContainerColor}>
				<QuestionText>
					<AssociationContainer>
						<AssociationColumns currentQuestion={currentQuestion} open />
					</AssociationContainer>
				</QuestionText>
			</QuestionContainer>
			<AnswerReveal
				revealKey={currentQuestion.uuid}
				answerHtml={currentQuestion.association_question?.answer || ''}
				explanation={currentQuestion.association_question?.explanation}
			/>
			<PointsRow
				points={currentQuestion.points}
				explanation={currentQuestion.points_explanation}
			/>
		</>
	);
};
